import * as React from "react";
import { Box, Typography, Stack, Button, Tooltip } from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import StarRating from "./StarRating";
import type { PayloadCategory } from "../../../types/payload";

export default function PayloadShowcase(props: { category: PayloadCategory }) {
  const { category } = props;

  return (
    <Stack spacing={1.75}>
      <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>
        {category.label} options
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: `repeat(${Math.min(category.options.length, 4)}, minmax(0, 1fr))` },
          gap: 2,
        }}
      >
        {category.options.map((option) => (
          <Box
            key={option.id}
            sx={{
              borderRadius: 2.5,
              border: "1px solid rgba(168,85,247,0.22)",
              background: "linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.015) 100%)",
              backdropFilter: "blur(14px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                height: 120,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(9,5,11,0.5)",
                borderBottom: "1px solid rgba(168,85,247,0.18)",
                backgroundImage: option.imageUrl ? `url(${option.imageUrl})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {!option.imageUrl && <ImageNotSupportedRoundedIcon sx={{ fontSize: 28, color: "rgba(226,232,240,0.3)" }} />}
            </Box>

            <Stack spacing={1.25} sx={{ p: 2, flex: 1 }}>
              <Stack spacing={0.25}>
                <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15 }}>{option.name}</Typography>
                {option.manufacturer && (
                  <Typography sx={{ color: "#A78BFA", fontWeight: 700, fontSize: 11.5, letterSpacing: "0.02em" }}>
                    {option.manufacturer}
                  </Typography>
                )}
                <Typography sx={{ color: "rgba(226,232,240,0.65)", fontSize: 12.5, lineHeight: 1.5 }}>
                  {option.summary}
                </Typography>
              </Stack>

              <Stack spacing={0.5}>
                <StarRating label={category.customCriterionLabel} value={option.ratings.resolution} />
                <StarRating label="Energy use" value={option.ratings.energy} />
                <StarRating label="Mass" value={option.ratings.mass} />
                <StarRating label="Cost" value={option.ratings.cost} />
                <StarRating label="TRL" value={option.ratings.trl} />
              </Stack>

              <Tooltip title={option.specSheetUrl ? "" : "Spec sheet not uploaded yet"}>
                <span>
                  <Button
                    fullWidth
                    size="small"
                    disabled={!option.specSheetUrl}
                    component={option.specSheetUrl ? "a" : "button"}
                    href={option.specSheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<PictureAsPdfRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      mt: "auto",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: 12.5,
                      borderRadius: 1.5,
                      color: "#E9D5FF",
                      border: "1px solid rgba(168,85,247,0.35)",
                      "&:hover": { bgcolor: "rgba(168,85,247,0.14)" },
                      "&.Mui-disabled": {
                        color: "rgba(226,232,240,0.35)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    Spec sheet
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
